import React, { useRef, useState } from "react";
import {
  Box,
  Text,
  Image,
  Button,
  useToast,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
} from "@chakra-ui/react";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const VisitingCardComponent = ({ userData, qrCodeData, onDownload }) => {
  const cardRef = useRef();
  const downloadRef = useRef(); // আলাদা ref ডাউনলোডের জন্য
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Default user data structure
  const defaultUser = {
    user_name: "",
    designation: "",
    user_phone: "",
    user_email: "",
    office_address: "",
    user_pic: "",
  };

  const user = { ...defaultUser, ...userData };

  const downloadJPG = async () => {
    if (!downloadRef.current) return;

    setIsLoading(true);
    try {
      const canvas = await html2canvas(downloadRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.download = `${user.user_name}_BA_visiting_card.jpg`;
      link.href = canvas.toDataURL();
      link.click();

      toast({
        title: "Downloaded Successfully",
        description: "Visiting Card has been downloaded as JPG",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download visiting card",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!downloadRef.current) return;

    setIsLoading(true);
    try {
      const canvas = await html2canvas(downloadRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Calculate dimensions to fit the cards properly
      const imgWidth = 180;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const x = (210 - imgWidth) / 2; // Center horizontally on A4
      const y = 20; // Start 20mm from top

      pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      pdf.save(`${user.user_name}_BA_visiting_card.pdf`);

      toast({
        title: "Downloaded Successfully",
        description: "Visiting Card has been downloaded as PDF",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download visiting card",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${user.user_name}
ORG:Business Automation Ltd.
TITLE:${user.designation}
TEL:${user.user_phone}
EMAIL:${user.user_email}
ADR:;;${user.office_address};;;;
URL:www.ba-systems.com
END:VCARD`;

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${user.user_name.replace(/\s+/g, "_")}_contact.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Contact Saved",
      description: "VCard has been downloaded",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const PerfectAlignedCard = ({ isPrimary = true }) => (
    <Box
      className={`ba-card-item ${
        isPrimary ? "ba-vcard-front" : "ba-vcard-back"
      }`}
      position="relative"
      w="372px"
      h="230px"
      display={isPrimary ? "block" : "flex"}
      flexDirection={isPrimary ? "initial" : "column"}
      borderRadius="8px"
      p="20px"
      boxSizing="border-box"
      background={isPrimary ? "#ffffff" : "#2B2E7D"}
      color={isPrimary ? "#000000" : "#ffffff"}
      border={isPrimary ? "1px solid #2B2E7D" : "none"}
    >
      {isPrimary ? (
        <>
          {/* Front Card Content */}
          <Flex
            className="card-front-head"
            position="relative"
            w="100%"
            justify="space-between"
            align="flex-start"
            h="95px"
          >
            <Box>
              <Text
                as="h3"
                fontFamily="'Roboto', sans-serif"
                m="0"
                p="0 0 2px"
                fontWeight="700"
                fontSize="14px"
                lineHeight="15px"
              >
                {user.user_name}
              </Text>
              <Text
                as="h4"
                fontFamily="'Roboto', sans-serif"
                m="0"
                p="0"
                fontWeight="500"
                fontSize="13px"
                lineHeight="1.2"
              >
                {user.designation}
              </Text>

              {/* Perfect Aligned Contact Icons */}
              <Box mt="10px">
                <Flex align="center" mb="2px" gap="8px">
                  <Box
                    w="14px"
                    h="14px"
                    pt={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14px"
                      viewBox="0 0 7 7"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_40_1185)">
                        <path
                          d="M1.40119 1.29701C1.11862 1.57958 0.971191 1.94815 0.971191 2.34129C0.971191 4.13501 3.35462 6.53072 5.16062 6.53072C5.55376 6.53072 5.92233 6.38329 6.19262 6.11301L6.42605 5.84272C6.69633 5.57243 6.69633 5.10558 6.42605 4.82301L5.84862 4.38072C5.56605 4.11044 5.12376 4.11044 4.84119 4.38072L4.49719 4.65101C3.74776 4.33158 3.20719 3.79101 2.86319 3.01701L3.13348 2.67301C3.40376 2.39043 3.40376 1.94815 3.13348 1.66558C3.13348 1.66558 2.70348 1.10043 2.69119 1.08815C2.40862 0.805578 1.94176 0.805578 1.65919 1.08815L1.41348 1.29701H1.40119ZM5.16062 6.05158C3.74776 6.05158 1.45033 3.97529 1.45033 2.34129C1.45033 2.07101 1.54862 1.82529 1.73291 1.64101L1.97862 1.43215C2.08919 1.32158 2.24891 1.32158 2.34719 1.40758C2.34719 1.40758 2.77719 1.97272 2.78948 1.97272C2.88776 2.07101 2.88776 2.23072 2.78948 2.34129L2.40862 2.82043C2.35948 2.89415 2.34719 2.98015 2.37176 3.05386C2.77719 4.06129 3.47748 4.76158 4.44805 5.13015C4.52176 5.15472 4.60776 5.14244 4.68148 5.09329L5.16062 4.71244C5.25891 4.61415 5.43091 4.61415 5.52919 4.71244L6.09433 5.15472C6.18033 5.25301 6.18033 5.41272 6.09433 5.51101L5.86091 5.78129C5.68891 5.95329 5.44319 6.05158 5.17291 6.05158H5.16062Z"
                          fill="#231F20"
                        />
                      </g>
                    </svg>
                  </Box>
                  <Text
                    fontSize="11px"
                    fontFamily="'Roboto', sans-serif"
                    lineHeight="1"
                  >
                    {user.user_phone}
                  </Text>
                </Flex>

                <Flex align="center" mb="2px" gap="8px">
                  <Box
                    w="14px"
                    h="14px"
                    pt={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <svg
                      width="16px"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 8 6"
                      fill="none"
                    >
                      <path
                        d="M5.86091 0.142578H2.26119C1.54862 0.142578 0.971191 0.720007 0.971191 1.43258V4.51629C0.971191 5.22886 1.54862 5.80629 2.26119 5.80629H5.86091C6.57348 5.80629 7.15091 5.22886 7.15091 4.51629V1.43258C7.15091 0.720007 6.57348 0.142578 5.86091 0.142578ZM2.26119 0.658578H5.86091C6.18033 0.658578 6.46291 0.85515 6.57348 1.13772L4.60776 3.10344C4.30062 3.39829 3.82148 3.39829 3.51433 3.10344L1.53633 1.13772C1.65919 0.842864 1.94176 0.658578 2.24891 0.658578H2.26119ZM5.86091 5.29029H2.26119C1.83119 5.29029 1.48719 4.94629 1.48719 4.51629V1.81344L3.14576 3.47201C3.64948 3.97572 4.46033 3.97572 4.96405 3.47201L6.62262 1.81344V4.51629C6.62262 4.94629 6.27862 5.29029 5.84862 5.29029H5.86091Z"
                        fill="#231F20"
                      />
                    </svg>
                  </Box>
                  <Text
                    fontSize="11px"
                    fontFamily="'Roboto', sans-serif"
                    lineHeight="1"
                  >
                    {user.user_email}
                  </Text>
                </Flex>
              </Box>
            </Box>
            <Box className="ba-front-logo" maxW="100px" display="block">
              <Image src="/png/BA.png" alt="BA-Logo" w="90px" />
            </Box>
          </Flex>

          {/* Card Front Bottom */}
          <Flex
            className="card-front-bottom"
            position="relative"
            w="100%"
            justify="space-between"
            align="flex-end"
            h="95px"
          >
            <Box className="card-office-info">
              <Flex align="center" mb="2px" gap="8px">
                <Box
                  w="14px"
                  h="14px"
                  pt={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16px"
                    viewBox="0 0 7 7"
                    fill="none"
                  >
                    <path
                      d="M4.74291 3.7802C4.74291 3.91534 4.63233 4.01363 4.50948 4.01363H4.27605C4.14091 4.01363 4.04262 3.90306 4.04262 3.7802C4.04262 3.65734 4.15319 3.54677 4.27605 3.54677H4.50948C4.64462 3.54677 4.74291 3.65734 4.74291 3.7802ZM5.45548 3.54677H5.22205C5.08691 3.54677 4.98862 3.65734 4.98862 3.7802C4.98862 3.90306 5.09919 4.01363 5.22205 4.01363H5.45548C5.59062 4.01363 5.68891 3.90306 5.68891 3.7802C5.68891 3.65734 5.57833 3.54677 5.45548 3.54677ZM4.50948 4.49277H4.27605C4.14091 4.49277 4.04262 4.60334 4.04262 4.7262C4.04262 4.84906 4.15319 4.95963 4.27605 4.95963H4.50948C4.64462 4.95963 4.74291 4.84906 4.74291 4.7262C4.74291 4.60334 4.63233 4.49277 4.50948 4.49277ZM5.45548 4.49277H5.22205C5.08691 4.49277 4.98862 4.60334 4.98862 4.7262C4.98862 4.84906 5.09919 4.95963 5.22205 4.95963H5.45548C5.59062 4.95963 5.68891 4.84906 5.68891 4.7262C5.68891 4.60334 5.57833 4.49277 5.45548 4.49277ZM4.50948 1.66706H4.27605C4.14091 1.66706 4.04262 1.77763 4.04262 1.90048C4.04262 2.02334 4.15319 2.13391 4.27605 2.13391H4.50948C4.64462 2.13391 4.74291 2.02334 4.74291 1.90048C4.74291 1.77763 4.63233 1.66706 4.50948 1.66706ZM5.45548 1.66706H5.22205C5.08691 1.66706 4.98862 1.77763 4.98862 1.90048C4.98862 2.02334 5.09919 2.13391 5.22205 2.13391H5.45548C5.59062 2.13391 5.68891 2.02334 5.68891 1.90048C5.68891 1.77763 5.57833 1.66706 5.45548 1.66706ZM4.50948 2.61306H4.27605C4.14091 2.61306 4.04262 2.72363 4.04262 2.84648C4.04262 2.96934 4.15319 3.07991 4.27605 3.07991H4.50948C4.64462 3.07991 4.74291 2.96934 4.74291 2.84648C4.74291 2.72363 4.63233 2.61306 4.50948 2.61306ZM5.45548 2.61306H5.22205C5.08691 2.61306 4.98862 2.72363 4.98862 2.84648C4.98862 2.96934 5.09919 3.07991 5.22205 3.07991H5.45548C5.59062 3.07991 5.68891 2.96934 5.68891 2.84648C5.68891 2.72363 5.57833 2.61306 5.45548 2.61306ZM6.63491 1.66706V4.95963C6.63491 5.61077 6.10662 6.13906 5.45548 6.13906H2.15062C1.49948 6.13906 0.971191 5.61077 0.971191 4.95963V2.6622C0.971191 2.34277 1.10633 2.03563 1.32748 1.81448L1.76976 1.38448C2.04005 1.1142 2.47005 1.1142 2.75262 1.38448L3.08433 1.70391V1.65477C3.08433 1.00363 3.61262 0.475342 4.26376 0.475342H5.44319C6.09433 0.475342 6.62262 1.00363 6.62262 1.65477L6.63491 1.66706ZM3.09662 2.67448C3.09662 2.47791 3.02291 2.29363 2.87548 2.17077L2.43319 1.74077C2.43319 1.74077 2.32262 1.67934 2.27348 1.67934C2.22433 1.67934 2.15062 1.70391 2.11376 1.74077L1.67148 2.17077C1.53633 2.30591 1.45033 2.4902 1.45033 2.67448V4.97191C1.45033 5.36506 1.76976 5.68448 2.16291 5.68448H3.10891V2.6622L3.09662 2.67448ZM6.15576 1.66706C6.15576 1.27391 5.83633 0.954485 5.44319 0.954485H4.26376C3.87062 0.954485 3.55119 1.27391 3.55119 1.66706V5.6722H5.43091C5.82405 5.6722 6.14348 5.35277 6.14348 4.95963V1.66706H6.15576ZM2.38405 3.54677H2.15062C2.01548 3.54677 1.91719 3.65734 1.91719 3.7802C1.91719 3.90306 2.02776 4.01363 2.15062 4.01363H2.38405C2.51919 4.01363 2.61748 3.90306 2.61748 3.7802C2.61748 3.65734 2.50691 3.54677 2.38405 3.54677ZM2.38405 2.60077H2.15062C2.01548 2.60077 1.91719 2.71134 1.91719 2.8342C1.91719 2.95706 2.02776 3.06763 2.15062 3.06763H2.38405C2.51919 3.06763 2.61748 2.95706 2.61748 2.8342C2.61748 2.71134 2.50691 2.60077 2.38405 2.60077ZM2.38405 4.48048H2.15062C2.01548 4.48048 1.91719 4.59106 1.91719 4.71391C1.91719 4.83677 2.02776 4.94734 2.15062 4.94734H2.38405C2.51919 4.94734 2.61748 4.83677 2.61748 4.71391C2.61748 4.59106 2.50691 4.48048 2.38405 4.48048Z"
                      fill="#010101"
                    ></path>
                  </svg>
                </Box>
                <Text
                  fontSize="11px"
                  fontFamily="'Roboto', sans-serif"
                  fontWeight="700"
                  lineHeight="1"
                >
                  Business Automation Ltd.
                </Text>
              </Flex>

              <Flex align="center" mb="2px" gap="8px">
                <Box
                  w="14px"
                  h="14px"
                  pt={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16px"
                    viewBox="0 0 7 6"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_40_1179)">
                      <path
                        d="M3.79707 1.677C3.28107 1.677 2.85107 2.09472 2.85107 2.623C2.85107 3.15129 3.26879 3.569 3.79707 3.569C4.32536 3.569 4.74307 3.15129 4.74307 2.623C4.74307 2.09472 4.32536 1.677 3.79707 1.677ZM3.79707 3.08986C3.53907 3.08986 3.33022 2.881 3.33022 2.623C3.33022 2.365 3.53907 2.15614 3.79707 2.15614C4.05507 2.15614 4.26393 2.365 4.26393 2.623C4.26393 2.881 4.05507 3.08986 3.79707 3.08986Z"
                        fill="#231F20"
                      ></path>
                      <path
                        d="M3.79692 5.91559C3.3915 5.91559 3.01064 5.71902 2.77721 5.39959C1.88035 4.15873 1.42578 3.2373 1.42578 2.6353C1.42578 1.32073 2.49464 0.26416 3.79692 0.26416C5.1115 0.26416 6.16807 1.32073 6.16807 2.6353C6.16807 3.2373 5.7135 4.15873 4.81664 5.39959C4.58321 5.7313 4.20235 5.91559 3.79692 5.91559ZM3.79692 0.78016C2.77721 0.78016 1.94178 1.61559 1.94178 2.6353C1.94178 3.11445 2.38407 3.98673 3.19492 5.10473C3.44064 5.43645 3.89521 5.51016 4.22692 5.26445C4.28835 5.2153 4.34978 5.16616 4.38664 5.10473C5.1975 3.98673 5.63978 3.11445 5.63978 2.6353C5.63978 1.61559 4.80435 0.78016 3.78464 0.78016H3.79692Z"
                        fill="#231F20"
                      ></path>
                    </g>
                  </svg>
                </Box>
                <Text
                  fontSize="11px"
                  fontFamily="'Roboto', sans-serif"
                  lineHeight="1"
                >
                  {user.office_address}
                </Text>
              </Flex>

              <Flex align="center" mb="2px" gap="8px">
                <Box
                  w="14px"
                  h="14px"
                  pt={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16px"
                    viewBox="0 0 7 7"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_40_1185)">
                      <path
                        d="M1.40119 1.29701C1.11862 1.57958 0.971191 1.94815 0.971191 2.34129C0.971191 4.13501 3.35462 6.53072 5.16062 6.53072C5.55376 6.53072 5.92233 6.38329 6.19262 6.11301L6.42605 5.84272C6.69633 5.57243 6.69633 5.10558 6.42605 4.82301L5.84862 4.38072C5.56605 4.11044 5.12376 4.11044 4.84119 4.38072L4.49719 4.65101C3.74776 4.33158 3.20719 3.79101 2.86319 3.01701L3.13348 2.67301C3.40376 2.39043 3.40376 1.94815 3.13348 1.66558C3.13348 1.66558 2.70348 1.10043 2.69119 1.08815C2.40862 0.805578 1.94176 0.805578 1.65919 1.08815L1.41348 1.29701H1.40119ZM5.16062 6.05158C3.74776 6.05158 1.45033 3.97529 1.45033 2.34129C1.45033 2.07101 1.54862 1.82529 1.73291 1.64101L1.97862 1.43215C2.08919 1.32158 2.24891 1.32158 2.34719 1.40758C2.34719 1.40758 2.77719 1.97272 2.78948 1.97272C2.88776 2.07101 2.88776 2.23072 2.78948 2.34129L2.40862 2.82043C2.35948 2.89415 2.34719 2.98015 2.37176 3.05386C2.77719 4.06129 3.47748 4.76158 4.44805 5.13015C4.52176 5.15472 4.60776 5.14244 4.68148 5.09329L5.16062 4.71244C5.25891 4.61415 5.43091 4.61415 5.52919 4.71244L6.09433 5.15472C6.18033 5.25301 6.18033 5.41272 6.09433 5.51101L5.86091 5.78129C5.68891 5.95329 5.44319 6.05158 5.17291 6.05158H5.16062Z"
                        fill="#231F20"
                      />
                    </g>
                  </svg>
                </Box>
                <Text
                  fontSize="11px"
                  fontFamily="'Roboto', sans-serif"
                  lineHeight="1"
                >
                  Telephone: +880-9602-666777
                </Text>
              </Flex>
            </Box>

            <Box className="ba-front-logo" minW="60px">
              <QRCode
                value={
                  qrCodeData ||
                  `${window.location.origin}/vc/${
                    user.user_email?.replace("@ba-systems.com", "") || "unknown"
                  }`
                }
                size={60}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </Box>
          </Flex>
        </>
      ) : (
        <>
          {/* Back Card Content */}
          <Box
            className="card-back-head"
            display="flex"
            flexDirection="column"
            textAlign="center"
            pt="5px"
            mb="15px"
          >
            <Text
              as="h4"
              fontFamily="'Roboto', sans-serif"
              m="0"
              p="0"
              fontWeight="700"
              fontSize="13px"
              lineHeight="1.2"
            >
              {user.user_name}
            </Text>
            <Text fontFamily="'Roboto', sans-serif" fontSize="12px">
              {user.designation}
            </Text>
          </Box>

          <Flex
            className="card-back-block"
            w="100%"
            align="center"
            justify="center"
            flex="1"
            p="0 25px"
          >
            <Box className="card-ba-logo">
              <Image src="/svg/ba-logo.svg" alt="BA-Logo" w="85px" />
            </Box>
          </Flex>

          <Flex
            className="card-back-block"
            w="100%"
            align="center"
            justify="space-between"
            p="0 25px"
            mt="auto"
            mb="00px"
          >
            <Box className="card-back-logo-item">
              <Image
                src="/svg/Insightdb.svg"
                alt="Insightdb"
                h="30px"
                w="90px"
              />
            </Box>
            <Box className="card-back-logo-item">
              <Image src="/svg/EBS.svg" alt="EBS" h="30px" w="60px" />
            </Box>
            <Box className="card-back-logo-item">
              <Image
                src="/svg/QueuePro.svg"
                alt="Queue-Pro"
                h="35px"
                w="45px"
              />
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );

  if (!user.user_name) {
    return (
      <Center minH="100vh">
        <Alert status="error" w="400px">
          <AlertIcon />
          <Box>
            <AlertTitle>No User Data!</AlertTitle>
            <AlertDescription>
              Please provide user data to display the visiting card.
            </AlertDescription>
          </Box>
        </Alert>
      </Center>
    );
  }

  return (
    <>
      {/* Hidden div শুধুমাত্র ডাউনলোডের জন্য - Perfect Alignment সহ */}
      <Box
        ref={downloadRef}
        position="absolute"
        left="-9999px"
        top="0"
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="20px"
        p="80px 100px"
        w="650px"
        boxSizing="border-box"
        background="#ffffff"
        fontFamily="'Roboto', sans-serif"
      >
        <PerfectAlignedCard isPrimary={true} />
        <PerfectAlignedCard isPrimary={false} />
      </Box>

      {/* Main UI */}
      <Box
        className="ba-visiting-cards-sec"
        position="relative"
        w="100%"
        p="40px 0"
        fontFamily="'Roboto', sans-serif"
        fontSize="14px"
        lineHeight="18px"
        fontWeight="400"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
      >
        <Box
          className="ba-vcard-container"
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="100%"
          flexWrap="wrap"
          gap="15px"
          ref={cardRef}
        >
          {/* Front Card */}
          <Box
            className="ba-card-item ba-vcard-front"
            position="relative"
            w="372px"
            h="230px"
            display="block"
            borderRadius="8px"
            p="20px"
            boxSizing="border-box"
            background="#ffffff"
            color="#000000"
            border="1px solid #2B2E7D"
          >
            {/* Card Front Head */}
            <Flex
              className="card-front-head"
              position="relative"
              w="100%"
              justify="space-between"
              align="flex-start"
              h="95px"
            >
              <Box>
                <Text
                  as="h3"
                  fontFamily="'Roboto', sans-serif"
                  m="0"
                  p="0 0 2px"
                  fontWeight="700"
                  fontSize="14px"
                  lineHeight="15px"
                >
                  {user.user_name}
                </Text>
                <Text
                  as="h4"
                  fontFamily="'Roboto', sans-serif"
                  m="0"
                  p="0"
                  fontWeight="500"
                  fontSize="13px"
                  lineHeight="1.2"
                >
                  {user.designation}
                </Text>

                {/* Contact Icons */}
                <Box mt="10px">
                  <Flex
                    className="card-icon-block"
                    align="center"
                    m="2px 0"
                    position="relative"
                    pl="20px"
                    fontFamily="'Roboto', sans-serif"
                    fontSize="11px"
                    lineHeight="1.3"
                    fontWeight="400"
                    minH="16px"
                  >
                    <Box
                      className="card-info-icon"
                      position="absolute"
                      left="0"
                      top="0"
                      h="13px"
                      w="18px"
                      display="flex"
                      align="center"
                      justify="flex-start"
                    >
                      {/* Phone SVG */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14px"
                        viewBox="0 0 7 7"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_40_1185)">
                          <path
                            d="M1.40119 1.29701C1.11862 1.57958 0.971191 1.94815 0.971191 2.34129C0.971191 4.13501 3.35462 6.53072 5.16062 6.53072C5.55376 6.53072 5.92233 6.38329 6.19262 6.11301L6.42605 5.84272C6.69633 5.57243 6.69633 5.10558 6.42605 4.82301L5.84862 4.38072C5.56605 4.11044 5.12376 4.11044 4.84119 4.38072L4.49719 4.65101C3.74776 4.33158 3.20719 3.79101 2.86319 3.01701L3.13348 2.67301C3.40376 2.39043 3.40376 1.94815 3.13348 1.66558C3.13348 1.66558 2.70348 1.10043 2.69119 1.08815C2.40862 0.805578 1.94176 0.805578 1.65919 1.08815L1.41348 1.29701H1.40119ZM5.16062 6.05158C3.74776 6.05158 1.45033 3.97529 1.45033 2.34129C1.45033 2.07101 1.54862 1.82529 1.73291 1.64101L1.97862 1.43215C2.08919 1.32158 2.24891 1.32158 2.34719 1.40758C2.34719 1.40758 2.77719 1.97272 2.78948 1.97272C2.88776 2.07101 2.88776 2.23072 2.78948 2.34129L2.40862 2.82043C2.35948 2.89415 2.34719 2.98015 2.37176 3.05386C2.77719 4.06129 3.47748 4.76158 4.44805 5.13015C4.52176 5.15472 4.60776 5.14244 4.68148 5.09329L5.16062 4.71244C5.25891 4.61415 5.43091 4.61415 5.52919 4.71244L6.09433 5.15472C6.18033 5.25301 6.18033 5.41272 6.09433 5.51101L5.86091 5.78129C5.68891 5.95329 5.44319 6.05158 5.17291 6.05158H5.16062Z"
                            fill="#231F20"
                          />
                        </g>
                      </svg>
                    </Box>
                    <Text className="card-info-desc">{user.user_phone}</Text>
                  </Flex>

                  <Flex
                    className="card-icon-block"
                    align="center"
                    m="2px 0"
                    position="relative"
                    pl="20px"
                    fontFamily="'Roboto', sans-serif"
                    fontSize="11px"
                    lineHeight="1.3"
                    fontWeight="400"
                    minH="16px"
                  >
                    <Box
                      className="card-info-icon"
                      position="absolute"
                      left="0"
                      top="0"
                      h="13px"
                      w="18px"
                      display="flex"
                      align="center"
                      justify="flex-start"
                    >
                      {/* Email SVG */}
                      <svg
                        width="16px"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 8 6"
                        fill="none"
                      >
                        <path
                          d="M5.86091 0.142578H2.26119C1.54862 0.142578 0.971191 0.720007 0.971191 1.43258V4.51629C0.971191 5.22886 1.54862 5.80629 2.26119 5.80629H5.86091C6.57348 5.80629 7.15091 5.22886 7.15091 4.51629V1.43258C7.15091 0.720007 6.57348 0.142578 5.86091 0.142578ZM2.26119 0.658578H5.86091C6.18033 0.658578 6.46291 0.85515 6.57348 1.13772L4.60776 3.10344C4.30062 3.39829 3.82148 3.39829 3.51433 3.10344L1.53633 1.13772C1.65919 0.842864 1.94176 0.658578 2.24891 0.658578H2.26119ZM5.86091 5.29029H2.26119C1.83119 5.29029 1.48719 4.94629 1.48719 4.51629V1.81344L3.14576 3.47201C3.64948 3.97572 4.46033 3.97572 4.96405 3.47201L6.62262 1.81344V4.51629C6.62262 4.94629 6.27862 5.29029 5.84862 5.29029H5.86091Z"
                          fill="#231F20"
                        />
                      </svg>
                    </Box>
                    <Text className="card-info-desc">{user.user_email}</Text>
                  </Flex>
                </Box>
              </Box>
              <Box className="ba-front-logo" maxW="100px" display="block">
                <Image src="/png/BA.png" alt="BA-Logo" w="90px" />
              </Box>
            </Flex>

            {/* Card Front Bottom */}
            <Flex
              className="card-front-bottom"
              position="relative"
              w="100%"
              justify="space-between"
              align="flex-end"
              h="95px"
            >
              <Box className="card-office-info">
                <Flex
                  className="card-icon-block"
                  align="center"
                  m="2px 0"
                  position="relative"
                  pl="20px"
                  fontFamily="'Roboto', sans-serif"
                  fontSize="11px"
                  lineHeight="1.3"
                  fontWeight="400"
                  minH="16px"
                >
                  <Box
                    className="card-info-icon"
                    position="absolute"
                    left="0"
                    top="0"
                    h="13px"
                    w="18px"
                    display="flex"
                    align="center"
                    justify="flex-start"
                  >
                    {/* Building SVG */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16px"
                      viewBox="0 0 7 7"
                      fill="none"
                    >
                      <path
                        d="M4.74291 3.7802C4.74291 3.91534 4.63233 4.01363 4.50948 4.01363H4.27605C4.14091 4.01363 4.04262 3.90306 4.04262 3.7802C4.04262 3.65734 4.15319 3.54677 4.27605 3.54677H4.50948C4.64462 3.54677 4.74291 3.65734 4.74291 3.7802ZM5.45548 3.54677H5.22205C5.08691 3.54677 4.98862 3.65734 4.98862 3.7802C4.98862 3.90306 5.09919 4.01363 5.22205 4.01363H5.45548C5.59062 4.01363 5.68891 3.90306 5.68891 3.7802C5.68891 3.65734 5.57833 3.54677 5.45548 3.54677ZM4.50948 4.49277H4.27605C4.14091 4.49277 4.04262 4.60334 4.04262 4.7262C4.04262 4.84906 4.15319 4.95963 4.27605 4.95963H4.50948C4.64462 4.95963 4.74291 4.84906 4.74291 4.7262C4.74291 4.60334 4.63233 4.49277 4.50948 4.49277ZM5.45548 4.49277H5.22205C5.08691 4.49277 4.98862 4.60334 4.98862 4.7262C4.98862 4.84906 5.09919 4.95963 5.22205 4.95963H5.45548C5.59062 4.95963 5.68891 4.84906 5.68891 4.7262C5.68891 4.60334 5.57833 4.49277 5.45548 4.49277ZM4.50948 1.66706H4.27605C4.14091 1.66706 4.04262 1.77763 4.04262 1.90048C4.04262 2.02334 4.15319 2.13391 4.27605 2.13391H4.50948C4.64462 2.13391 4.74291 2.02334 4.74291 1.90048C4.74291 1.77763 4.63233 1.66706 4.50948 1.66706ZM5.45548 1.66706H5.22205C5.08691 1.66706 4.98862 1.77763 4.98862 1.90048C4.98862 2.02334 5.09919 2.13391 5.22205 2.13391H5.45548C5.59062 2.13391 5.68891 2.02334 5.68891 1.90048C5.68891 1.77763 5.57833 1.66706 5.45548 1.66706ZM4.50948 2.61306H4.27605C4.14091 2.61306 4.04262 2.72363 4.04262 2.84648C4.04262 2.96934 4.15319 3.07991 4.27605 3.07991H4.50948C4.64462 3.07991 4.74291 2.96934 4.74291 2.84648C4.74291 2.72363 4.63233 2.61306 4.50948 2.61306ZM5.45548 2.61306H5.22205C5.08691 2.61306 4.98862 2.72363 4.98862 2.84648C4.98862 2.96934 5.09919 3.07991 5.22205 3.07991H5.45548C5.59062 3.07991 5.68891 2.96934 5.68891 2.84648C5.68891 2.72363 5.57833 2.61306 5.45548 2.61306ZM6.63491 1.66706V4.95963C6.63491 5.61077 6.10662 6.13906 5.45548 6.13906H2.15062C1.49948 6.13906 0.971191 5.61077 0.971191 4.95963V2.6622C0.971191 2.34277 1.10633 2.03563 1.32748 1.81448L1.76976 1.38448C2.04005 1.1142 2.47005 1.1142 2.75262 1.38448L3.08433 1.70391V1.65477C3.08433 1.00363 3.61262 0.475342 4.26376 0.475342H5.44319C6.09433 0.475342 6.62262 1.00363 6.62262 1.65477L6.63491 1.66706ZM3.09662 2.67448C3.09662 2.47791 3.02291 2.29363 2.87548 2.17077L2.43319 1.74077C2.43319 1.74077 2.32262 1.67934 2.27348 1.67934C2.22433 1.67934 2.15062 1.70391 2.11376 1.74077L1.67148 2.17077C1.53633 2.30591 1.45033 2.4902 1.45033 2.67448V4.97191C1.45033 5.36506 1.76976 5.68448 2.16291 5.68448H3.10891V2.6622L3.09662 2.67448ZM6.15576 1.66706C6.15576 1.27391 5.83633 0.954485 5.44319 0.954485H4.26376C3.87062 0.954485 3.55119 1.27391 3.55119 1.66706V5.6722H5.43091C5.82405 5.6722 6.14348 5.35277 6.14348 4.95963V1.66706H6.15576ZM2.38405 3.54677H2.15062C2.01548 3.54677 1.91719 3.65734 1.91719 3.7802C1.91719 3.90306 2.02776 4.01363 2.15062 4.01363H2.38405C2.51919 4.01363 2.61748 3.90306 2.61748 3.7802C2.61748 3.65734 2.50691 3.54677 2.38405 3.54677ZM2.38405 2.60077H2.15062C2.01548 2.60077 1.91719 2.71134 1.91719 2.8342C1.91719 2.95706 2.02776 3.06763 2.15062 3.06763H2.38405C2.51919 3.06763 2.61748 2.95706 2.61748 2.8342C2.61748 2.71134 2.50691 2.60077 2.38405 2.60077ZM2.38405 4.48048H2.15062C2.01548 4.48048 1.91719 4.59106 1.91719 4.71391C1.91719 4.83677 2.02776 4.94734 2.15062 4.94734H2.38405C2.51919 4.94734 2.61748 4.83677 2.61748 4.71391C2.61748 4.59106 2.50691 4.48048 2.38405 4.48048Z"
                        fill="#010101"
                      ></path>
                    </svg>
                  </Box>
                  <Text
                    as="h3"
                    className="card-info-desc"
                    fontWeight="700"
                    fontFamily="'Roboto', sans-serif"
                  >
                    Business Automation Ltd.
                  </Text>
                </Flex>

                <Flex
                  className="card-icon-block"
                  align="center"
                  m="2px 0"
                  position="relative"
                  pl="20px"
                  fontFamily="'Roboto', sans-serif"
                  fontSize="11px"
                  lineHeight="1.3"
                  fontWeight="400"
                  minH="16px"
                >
                  <Box
                    className="card-info-icon"
                    position="absolute"
                    left="0"
                    top="0"
                    h="13px"
                    w="18px"
                    display="flex"
                    align="center"
                    justify="flex-start"
                  >
                    {/* Location SVG */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16px"
                      viewBox="0 0 7 6"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_40_1179)">
                        <path
                          d="M3.79707 1.677C3.28107 1.677 2.85107 2.09472 2.85107 2.623C2.85107 3.15129 3.26879 3.569 3.79707 3.569C4.32536 3.569 4.74307 3.15129 4.74307 2.623C4.74307 2.09472 4.32536 1.677 3.79707 1.677ZM3.79707 3.08986C3.53907 3.08986 3.33022 2.881 3.33022 2.623C3.33022 2.365 3.53907 2.15614 3.79707 2.15614C4.05507 2.15614 4.26393 2.365 4.26393 2.623C4.26393 2.881 4.05507 3.08986 3.79707 3.08986Z"
                          fill="#231F20"
                        ></path>
                        <path
                          d="M3.79692 5.91559C3.3915 5.91559 3.01064 5.71902 2.77721 5.39959C1.88035 4.15873 1.42578 3.2373 1.42578 2.6353C1.42578 1.32073 2.49464 0.26416 3.79692 0.26416C5.1115 0.26416 6.16807 1.32073 6.16807 2.6353C6.16807 3.2373 5.7135 4.15873 4.81664 5.39959C4.58321 5.7313 4.20235 5.91559 3.79692 5.91559ZM3.79692 0.78016C2.77721 0.78016 1.94178 1.61559 1.94178 2.6353C1.94178 3.11445 2.38407 3.98673 3.19492 5.10473C3.44064 5.43645 3.89521 5.51016 4.22692 5.26445C4.28835 5.2153 4.34978 5.16616 4.38664 5.10473C5.1975 3.98673 5.63978 3.11445 5.63978 2.6353C5.63978 1.61559 4.80435 0.78016 3.78464 0.78016H3.79692Z"
                          fill="#231F20"
                        ></path>
                      </g>
                      <defs>
                        <clipPath id="clip0_40_1179">
                          <rect
                            width="5.65143"
                            height="5.65143"
                            fill="white"
                            transform="translate(0.971191 0.26416)"
                          ></rect>
                        </clipPath>
                      </defs>
                    </svg>
                  </Box>
                  <Text className="card-info-desc">{user.office_address}</Text>
                </Flex>

                <Flex
                  className="card-icon-block"
                  align="center"
                  m="2px 0"
                  position="relative"
                  pl="20px"
                  fontFamily="'Roboto', sans-serif"
                  fontSize="11px"
                  lineHeight="1.3"
                  fontWeight="400"
                  minH="16px"
                >
                  <Box
                    className="card-info-icon"
                    position="absolute"
                    left="0"
                    top="0"
                    h="13px"
                    w="18px"
                    display="flex"
                    align="center"
                    justify="flex-start"
                  >
                    {/* Phone SVG */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16px"
                      viewBox="0 0 7 7"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_40_1185)">
                        <path
                          d="M1.40119 1.29701C1.11862 1.57958 0.971191 1.94815 0.971191 2.34129C0.971191 4.13501 3.35462 6.53072 5.16062 6.53072C5.55376 6.53072 5.92233 6.38329 6.19262 6.11301L6.42605 5.84272C6.69633 5.57243 6.69633 5.10558 6.42605 4.82301L5.84862 4.38072C5.56605 4.11044 5.12376 4.11044 4.84119 4.38072L4.49719 4.65101C3.74776 4.33158 3.20719 3.79101 2.86319 3.01701L3.13348 2.67301C3.40376 2.39043 3.40376 1.94815 3.13348 1.66558C3.13348 1.66558 2.70348 1.10043 2.69119 1.08815C2.40862 0.805578 1.94176 0.805578 1.65919 1.08815L1.41348 1.29701H1.40119ZM5.16062 6.05158C3.74776 6.05158 1.45033 3.97529 1.45033 2.34129C1.45033 2.07101 1.54862 1.82529 1.73291 1.64101L1.97862 1.43215C2.08919 1.32158 2.24891 1.32158 2.34719 1.40758C2.34719 1.40758 2.77719 1.97272 2.78948 1.97272C2.88776 2.07101 2.88776 2.23072 2.78948 2.34129L2.40862 2.82043C2.35948 2.89415 2.34719 2.98015 2.37176 3.05386C2.77719 4.06129 3.47748 4.76158 4.44805 5.13015C4.52176 5.15472 4.60776 5.14244 4.68148 5.09329L5.16062 4.71244C5.25891 4.61415 5.43091 4.61415 5.52919 4.71244L6.09433 5.15472C6.18033 5.25301 6.18033 5.41272 6.09433 5.51101L5.86091 5.78129C5.68891 5.95329 5.44319 6.05158 5.17291 6.05158H5.16062Z"
                          fill="#231F20"
                        />
                      </g>
                    </svg>
                  </Box>
                  <Text className="card-info-desc">
                    Telephone: +880-9602-666777
                  </Text>
                </Flex>
              </Box>

              <Box className="ba-front-logo" minW="60px">
                <QRCode
                  value={
                    qrCodeData ||
                    `${window.location.origin}/vc/${
                      user.user_email?.replace("@ba-systems.com", "") ||
                      "unknown"
                    }`
                  }
                  size={60}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </Box>
            </Flex>
          </Box>

          {/* Back Card */}
          <Box
            className="ba-card-item ba-vcard-back"
            position="relative"
            w="372px"
            h="230px"
            display="flex"
            flexDirection="column"
            borderRadius="8px"
            p="20px"
            boxSizing="border-box"
            background="#2B2E7D"
            color="#ffffff"
          >
            {/* Header Section */}
            <Box
              className="card-back-head"
              display="flex"
              flexDirection="column"
              textAlign="center"
              pt="5px"
              mb="15px"
            >
              <Text
                as="h4"
                fontFamily="'Roboto', sans-serif"
                m="0"
                p="0"
                fontWeight="700"
                fontSize="13px"
                lineHeight="1.2"
              >
                {user.user_name}
              </Text>
              <Text fontFamily="'Roboto', sans-serif" fontSize="12px">
                {user.designation}
              </Text>
            </Box>

            {/* Main Logo Section - Center */}
            <Flex
              className="card-back-block"
              w="100%"
              align="center"
              justify="center"
              pb={5}
              flex="1"
              p="0 25px"
            >
              <Box className="card-ba-logo" pb={5}>
                <Image src="/svg/ba-logo.svg" alt="BA-Logo" w="85px" />
              </Box>
            </Flex>

            {/* Partner Logos Section - Bottom */}
            <Flex
              className="card-back-block"
              w="100%"
              align="center"
              justify="space-between"
              p="0 25px"
              mt="auto"
              mb="00px"
            >
              <Box className="card-back-logo-item">
                <Image
                  src="/svg/Insightdb.svg"
                  alt="Insightdb"
                  h="30px"
                  w="90px"
                />
              </Box>
              <Box className="card-back-logo-item">
                <Image src="/svg/EBS.svg" alt="EBS" h="30px" w="60px" />
              </Box>
              <Box className="card-back-logo-item">
                <Image
                  src="/svg/QueuePro.svg"
                  alt="Queue-Pro"
                  h="35px"
                  w="45px"
                />
              </Box>
            </Flex>
          </Box>
        </Box>

        {/* Button Group */}
        <Flex
          className="ba-card-btn-group"
          gap="10px"
          mt="30px"
          justify="center"
          wrap="wrap"
        >
          <Button
            className="vcard-btn-item"
            onClick={downloadJPG}
            isLoading={isLoading}
            bg="#2B2E7D"
            color="white"
            _hover={{ bg: "#1e2059" }}
            leftIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 14 14"
                fill="none"
              >
                <g clipPath="url(#clip0_34_6608)">
                  <path
                    d="M7.01762 10.4619C7.2666 10.4623 7.51321 10.4136 7.74327 10.3184C7.97334 10.2232 8.18233 10.0835 8.35824 9.90726L10.481 7.78447L9.33216 6.6356L7.82524 8.14252L7.81224 0.164307H6.18724L6.20024 8.13168L4.70307 6.6356L3.5542 7.78447L5.67699 9.90726C5.85286 10.0835 6.06185 10.2233 6.29192 10.3185C6.522 10.4136 6.76863 10.4624 7.01762 10.4619Z"
                    fill="white"
                  />
                  <path
                    d="M11.875 8.83081V11.5391H2.125V8.83081H0.5V11.5391C0.5 11.9701 0.671205 12.3834 0.975951 12.6882C1.2807 12.9929 1.69402 13.1641 2.125 13.1641H11.875C12.306 13.1641 12.7193 12.9929 13.024 12.6882C13.3288 12.3834 13.5 11.9701 13.5 11.5391V8.83081H11.875Z"
                    fill="white"
                  />
                </g>
              </svg>
            }
          >
            JPG
          </Button>

          <Button
            className="vcard-btn-item"
            onClick={downloadPDF}
            isLoading={isLoading}
            bg="#2B2E7D"
            color="white"
            _hover={{ bg: "#1e2059" }}
            leftIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 14 14"
                fill="none"
              >
                <g clipPath="url(#clip0_34_6608)">
                  <path
                    d="M7.01762 10.4619C7.2666 10.4623 7.51321 10.4136 7.74327 10.3184C7.97334 10.2232 8.18233 10.0835 8.35824 9.90726L10.481 7.78447L9.33216 6.6356L7.82524 8.14252L7.81224 0.164307H6.18724L6.20024 8.13168L4.70307 6.6356L3.5542 7.78447L5.67699 9.90726C5.85286 10.0835 6.06185 10.2233 6.29192 10.3185C6.522 10.4136 6.76863 10.4624 7.01762 10.4619Z"
                    fill="white"
                  />
                  <path
                    d="M11.875 8.83081V11.5391H2.125V8.83081H0.5V11.5391C0.5 11.9701 0.671205 12.3834 0.975951 12.6882C1.2807 12.9929 1.69402 13.1641 2.125 13.1641H11.875C12.306 13.1641 12.7193 12.9929 13.024 12.6882C13.3288 12.3834 13.5 11.9701 13.5 11.5391V8.83081H11.875Z"
                    fill="white"
                  />
                </g>
              </svg>
            }
          >
            PDF
          </Button>

          <Button
            className="vcard-btn-item"
            onClick={downloadVCard}
            bg="#2B2E7D"
            color="white"
            _hover={{ bg: "#1e2059" }}
            leftIcon={
              <svg
                width="13"
                height="14"
                viewBox="0 0 13 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_106_73)">
                  <path
                    d="M3.1001 6.66431C3.1001 6.94051 2.8763 7.16431 2.6001 7.16431H1.6001C1.32389 7.16431 1.1001 6.94051 1.1001 6.66431C1.1001 6.3881 1.32389 6.16431 1.6001 6.16431H2.6001C2.87633 6.16431 3.1001 6.3881 3.1001 6.66431ZM3.1001 3.46431C3.1001 3.1881 2.8763 2.96431 2.6001 2.96431H1.6001C1.32389 2.96431 1.1001 3.1881 1.1001 3.46431C1.1001 3.74051 1.32389 3.96431 1.6001 3.96431H2.6001C2.87633 3.96431 3.1001 3.74051 3.1001 3.46431ZM3.1001 9.86434C3.1001 9.58807 2.8763 9.36434 2.6001 9.36434H1.6001C1.32389 9.36434 1.1001 9.58807 1.1001 9.86434C1.1001 10.1405 1.32389 10.3643 1.6001 10.3643H2.6001C2.87633 10.3643 3.1001 10.1405 3.1001 9.86434ZM11.9001 1.76431V11.5643C11.9001 12.4479 11.1837 13.1643 10.3002 13.1643H3.30016C2.41657 13.1643 1.70016 12.4479 1.70016 11.5643V10.7643H2.60016C3.09636 10.7643 3.50016 10.3605 3.50016 9.86431C3.50016 9.3681 3.09636 8.96425 2.60016 8.96425H1.7001V7.56425H2.6001C3.0963 7.56425 3.5001 7.16045 3.5001 6.66425C3.5001 6.16804 3.0963 5.76425 2.6001 5.76425H1.7001V4.36425H2.6001C3.0963 4.36425 3.5001 3.96045 3.5001 3.46425C3.5001 2.96804 3.0963 2.56425 2.6001 2.56425H1.7001V1.76431C1.7001 0.880718 2.41651 0.164307 3.3001 0.164307H10.3001C11.1837 0.164307 11.9001 0.880689 11.9001 1.76431ZM5.5573 4.9071C5.5573 5.7591 6.2481 6.4499 7.1001 6.4499C7.95213 6.4499 8.64289 5.7591 8.64289 4.9071C8.64289 4.0551 7.95213 3.36428 7.1001 3.36428C6.24813 3.36431 5.5573 4.0551 5.5573 4.9071ZM9.5001 8.67848C9.5001 7.73189 8.73251 6.96428 7.78589 6.96428H6.41451C5.46772 6.96428 4.7001 7.73192 4.7001 8.67848V9.36431H9.5001V8.67848Z"
                    fill="white"
                  />
                </g>
              </svg>
            }
          >
            Save Contact
          </Button>
        </Flex>
      </Box>
    </>
  );
};

export default VisitingCardComponent;
